import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { habitSessionsPerWeek, planShape } from '@goaltree/backend/convex/lib/planShape'
import type { TreeInput } from '@goaltree/backend/convex/nodes'
import { cn } from '@goaltree/ui/lib/utils'
import { ChevronDown, Flag, Repeat } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddNodeRow } from '@/components/add-node-row'
import { InlineEditor } from '@/components/inline-editor'
import type { NodeDetail } from '@/components/node-detail-sheet'
import { NodeDetailSheet } from '@/components/node-detail-sheet'
import { PlanTaskRow } from '@/components/plan-outline'
import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { periodLabel } from '@/lib/schedule'
import { EFFORT_MINUTES, exactTaskMinutes, formatMinutes, taskTimeLabel } from '@/lib/task-time'

type TreeTask = { title: string; effort: string; minutes?: number; own?: boolean }

const taskMinutes = (task: TreeTask) => exactTaskMinutes(task) ?? EFFORT_MINUTES[task.effort] ?? 60

type Props = {
  tree: TreeInput
  onUpdate?: (tree: TreeInput) => void
  // Fires as the goal title scrolls under the app header and back.
  onTitleHidden?: (hidden: boolean) => void
}

export default function HierarchyTree({ tree, onUpdate, onTitleHidden }: Props) {
  const { t, i18n } = useTranslation()
  const titleRef = useRef<HTMLHeadingElement>(null)
  const onTitleHiddenRef = useRef(onTitleHidden)
  onTitleHiddenRef.current = onTitleHidden
  useEffect(() => {
    const el = titleRef.current
    if (!el) return
    const hdr = parseFloat(getComputedStyle(el).getPropertyValue('--app-hdr-h')) || 56
    const observer = new IntersectionObserver(
      ([entry]) =>
        onTitleHiddenRef.current?.(!entry.isIntersecting && entry.boundingClientRect.top < hdr),
      { rootMargin: `-${hdr}px 0px 0px 0px` },
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      onTitleHiddenRef.current?.(false)
    }
  }, [])
  const color = CATEGORY_COLORS[tree.category?.toLowerCase() ?? ''] ?? '#6b7280'
  const editable = !!onUpdate
  const [detail, setDetail] = useState<{ node: NodeDetail; onDelete: () => void } | null>(null)

  function makeDetail(
    type: NodeDetail['type'],
    title: string,
    opts?: { milestoneTitle?: string; targetTitle?: string; effort?: 'low' | 'medium' | 'high' },
  ): NodeDetail {
    return {
      nodeId: title as unknown as Id<'nodes'>,
      type,
      title,
      category: tree.category,
      goalTitle: tree.title,
      ...opts,
    }
  }

  function openDetail(node: NodeDetail, onDelete: () => void) {
    setDetail({ node, onDelete })
  }

  function patch(fn: (t: TreeInput) => void) {
    if (!onUpdate) return
    const next = structuredClone(tree)
    fn(next)
    onUpdate(next)
  }

  const shape = planShape(tree)
  const flat = tree.milestones.flatMap((ms, msIdx) =>
    ms.targets.flatMap((target, tgIdx) =>
      target.tasks.map((task, taskIdx) => ({ ms, msIdx, target, tgIdx, task, taskIdx })),
    ),
  )
  const firstWeek = flat.filter((x) => x.msIdx === 0).slice(0, shape.weekTasks)
  // The habits run alongside the week's tasks, so they count toward it too.
  const firstWeekMinutes = Math.round(
    firstWeek.reduce((n, x) => n + taskMinutes(x.task), 0) +
      (tree.habits ?? []).reduce((n, h) => n + (h.minutes ?? 0) * habitSessionsPerWeek(h), 0),
  )
  const [open, setOpen] = useState<Set<number>>(() => new Set())
  const fmtDate = (ms: number) =>
    new Date(ms).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })

  function openTask(x: (typeof flat)[number]) {
    openDetail(
      makeDetail('task', x.task.title, {
        milestoneTitle: x.ms.title,
        targetTitle: x.target.title,
        effort: x.task.effort as 'low' | 'medium' | 'high',
      }),
      () =>
        patch((n) => {
          n.milestones[x.msIdx].targets[x.tgIdx].tasks.splice(x.taskIdx, 1)
        }),
    )
  }

  return (
    <>
      <NodeDetailSheet
        node={detail?.node ?? null}
        onClose={() => setDetail(null)}
        onDelete={detail?.onDelete}
        draft
      />
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2.5">
          <p
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: categoryTextColor(color) }}
          >
            {t(`goals.cat.${tree.category.toLowerCase()}`, { defaultValue: tree.category })}
            {shape.weeks && ` · ${t('planPreview.weekPlan', { count: shape.weeks })}`}
          </p>
          <h1
            ref={titleRef}
            className="text-3xl font-black leading-[1.1] tracking-tight text-balance text-foreground sm:text-4xl"
          >
            <InlineEditor
              value={tree.title.charAt(0).toUpperCase() + tree.title.slice(1)}
              onSave={async (v) =>
                patch((n) => {
                  n.title = v
                })
              }
              disabled={!editable}
              className="font-black leading-[1.1]"
            />
          </h1>
          <p className="text-sm text-muted-foreground">
            {[
              shape.perWeek && t('planPreview.perWeek', { n: shape.perWeek }),
              tree.dueDate && t('planPreview.doneBy', { date: fmtDate(tree.dueDate) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        {firstWeek.length > 0 && (
          <section className="rounded-2xl border border-border bg-card/60 p-4">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[15px] font-extrabold">{t('planPreview.thisWeek')}</h2>
              <span className="text-xs tabular-nums text-muted-foreground">
                {t('planPreview.sessions', { count: shape.perWeek ?? firstWeek.length })} ·{' '}
                {formatMinutes(firstWeekMinutes)}
              </span>
            </div>
            <ul className="mt-3 flex flex-col gap-1">
              {firstWeek.map((x, i) => (
                <li key={`${x.tgIdx}-${x.taskIdx}`}>
                  <button
                    type="button"
                    onClick={() => openTask(x)}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-lg py-1.5 text-left transition-colors hover:bg-muted/40"
                  >
                    <span className="flex h-9 w-[72px] shrink-0 items-center justify-center rounded-lg bg-muted/70 text-[11px] font-bold text-muted-foreground">
                      {t('planPreview.session', { n: i + 1 })}
                    </span>
                    <span className="min-w-0 flex-1 text-sm font-medium">{x.task.title}</span>
                    {x.task.own && <OwnBadge />}
                    <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                      {taskTimeLabel(x.task, '')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {!!tree.habits?.length && (
              <p className="mt-3 flex items-center gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
                <Repeat className="size-3.5 shrink-0 text-primary-text" />
                {t('planPreview.alongside', {
                  names: new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
                    tree.habits.map(
                      (h) => `${h.title} (${periodLabel(t, i18n.language, h.cadence, h.days)})`,
                    ),
                  ),
                })}
              </p>
            )}
          </section>
        )}

        <section className="flex flex-col gap-3">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
            {t('planPreview.roadAhead')}
          </h2>
          <div className="flex gap-1" aria-hidden>
            {tree.milestones.map((ms, i) => {
              const span = shape.spans[i]
              const grow = span
                ? span.to - span.from + 1
                : ms.targets.reduce((n, tg) => n + tg.tasks.length, 0) || 1
              return (
                <span
                  key={i}
                  className={cn('h-1.5 rounded-full', i === 0 ? 'bg-primary' : 'bg-muted')}
                  style={{ flexGrow: grow }}
                />
              )
            })}
          </div>
          <ol className="mt-1 flex flex-col">
            {tree.milestones.map((ms, msIdx) => {
              const span = shape.spans[msIdx]
              const isOpen = open.has(msIdx)
              const last = msIdx === tree.milestones.length - 1
              const tasks = flat.filter((x) => x.msIdx === msIdx)
              const toggle = () =>
                setOpen((prev) => {
                  const next = new Set(prev)
                  if (next.has(msIdx)) next.delete(msIdx)
                  else next.add(msIdx)
                  return next
                })
              return (
                <li
                  key={msIdx}
                  className="relative grid grid-cols-[28px_minmax(0,1fr)] gap-x-3 pb-4"
                >
                  {!last && (
                    <span
                      className="absolute top-8 bottom-0 left-[13px] w-0.5 bg-border"
                      aria-hidden
                    />
                  )}
                  <span
                    className={cn(
                      'flex size-7 items-center justify-center rounded-full text-xs font-extrabold',
                      msIdx === 0
                        ? 'bg-primary text-primary-foreground'
                        : 'border-2 border-border text-muted-foreground',
                    )}
                  >
                    {last && msIdx > 0 ? (
                      <Flag className="size-3.5" strokeWidth={2.5} />
                    ) : (
                      msIdx + 1
                    )}
                  </span>
                  <div className="min-w-0">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={toggle}
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2 text-left',
                        isOpen &&
                          'sticky top-[var(--app-hdr-h,0px)] z-10 -mt-1.5 bg-background py-1.5',
                      )}
                    >
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="text-[11px] font-semibold text-muted-foreground">
                          {[
                            span &&
                              (span.from === span.to
                                ? t('planPreview.week', { n: span.from })
                                : t('planPreview.weeks', { from: span.from, to: span.to })),
                            t('planPreview.sessions', { count: tasks.length }),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                        <span className="text-[17px] font-extrabold leading-snug">{ms.title}</span>
                      </span>
                      <ChevronDown
                        className={cn(
                          'size-4 shrink-0 text-muted-foreground transition-transform',
                          isOpen && 'rotate-180',
                        )}
                      />
                    </button>
                    {isOpen && (
                      <div className="mt-2">
                        <ul>
                          {tasks.map((x) => (
                            <TaskLi
                              key={`${x.tgIdx}-${x.taskIdx}`}
                              task={x.task}
                              editable={editable}
                              onDetails={() => openTask(x)}
                              onSave={(v) =>
                                patch((n) => {
                                  n.milestones[msIdx].targets[x.tgIdx].tasks[x.taskIdx].title = v
                                })
                              }
                            />
                          ))}
                        </ul>
                        {editable && (
                          <AddNodeRow
                            label={t('planPreview.addSession')}
                            onAdd={async () =>
                              patch((n) => {
                                const m = n.milestones[msIdx]
                                if (!m.targets.length) m.targets.push({ title: m.title, tasks: [] })
                                m.targets[m.targets.length - 1].tasks.push({
                                  title: '',
                                  effort: 'medium',
                                })
                              })
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
          {editable && (
            <AddNodeRow
              label={t('goals.addMilestone')}
              onAdd={async (title) =>
                patch((n) => {
                  n.milestones.push({ title, targets: [] })
                })
              }
            />
          )}
        </section>
      </div>
    </>
  )
}

function TaskLi({
  task,
  editable,
  onDetails,
  onSave,
}: {
  task: TreeTask
  editable: boolean
  onDetails: () => void
  onSave: (v: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  return (
    <PlanTaskRow
      onClick={() => {
        if (!editing) onDetails()
      }}
      title={
        <InlineEditor
          value={task.title}
          onSave={async (v) => onSave(v)}
          disabled={!editable || isTouch}
          className="block truncate text-sm font-medium"
          onEditingChange={setEditing}
        />
      }
      meta={
        task.own ? (
          <span className="flex items-center gap-1.5">
            <OwnBadge />
            {taskTimeLabel(task, '')}
          </span>
        ) : (
          taskTimeLabel(task, '')
        )
      }
    />
  )
}

// A task the user already had (from their photo), placed by the plan.
function OwnBadge() {
  const { t } = useTranslation()
  return (
    <span
      title={t('photoTasks.ownHint')}
      className="shrink-0 rounded-full bg-primary/10 px-1.5 py-px font-sans text-[10px] font-semibold text-primary-text"
    >
      {t('photoTasks.own')}
    </span>
  )
}
